import { useEffect, useMemo, useState } from 'react'
import { api, fmtSiren } from '../lib/api'
import type { Societe } from '../lib/api'
import { href } from '../lib/router'
import { IconSearch, IconChevronRight, IconBuilding } from '../components/Icons'

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; societes: Societe[] }

function classifyBand(s: Societe): 'blue' | 'amber' | 'green' | 'grey' {
  if (s.DateArchivageAppliquee) return 'grey'
  if (s.TypeSociete === 'BIC' && !s.Siren) return 'amber' // société de démo
  if (s.FormeJuridiqueAbregee?.startsWith('SC')) return 'green' // SCI, SCV
  return 'blue' // société standard
}

function bandLabel(s: Societe): string {
  if (s.DateArchivageAppliquee) return 'Archivée'
  if (s.TypeSociete === 'BIC' && !s.Siren) return 'Démo'
  if (s.FormeJuridiqueAbregee?.startsWith('SC')) return 'Immobilier'
  return 'Actif'
}

export function SocietesPage() {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [query, setQuery] = useState('')

  useEffect(() => {
    let alive = true
    api.societes().then(res => {
      if (!alive) return
      if (!res.ok) setState({ kind: 'error', message: res.error })
      else setState({ kind: 'ok', societes: res.data })
    })
    return () => { alive = false }
  }, [])

  const filtered = useMemo(() => {
    if (state.kind !== 'ok') return []
    const q = query.trim().toLowerCase()
    if (!q) return state.societes
    return state.societes.filter(s =>
      s.CodeClient.toLowerCase().includes(q) ||
      s.RaisonSociale.toLowerCase().includes(q) ||
      (s.Siren ?? '').includes(q) ||
      (s.Ville ?? '').toLowerCase().includes(q)
    )
  }, [state, query])

  return (
    <>
      <div className="filter-bar">
        <label className="filter-input">
          <IconSearch size={16} />
          <input
            type="search"
            placeholder="Rechercher une société (nom, code, SIREN, ville…)"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </label>
        <div className="filter-counter">
          {state.kind === 'ok' && (
            <span>
              <strong>{filtered.length}</strong> société{filtered.length > 1 ? 's' : ''}
              {filtered.length !== state.societes.length && ` sur ${state.societes.length}`}
            </span>
          )}
        </div>
      </div>

      {state.kind === 'loading' && (
        <div className="empty-state"><p>Chargement…</p></div>
      )}

      {state.kind === 'error' && (
        <div className="empty-state">
          <h2>Erreur de chargement</h2>
          <p>{state.message}</p>
        </div>
      )}

      {state.kind === 'ok' && filtered.length === 0 && (
        <div className="empty-state">
          <h2>Aucun résultat</h2>
          <p>Aucune société ne correspond à « {query} ».</p>
        </div>
      )}

      {state.kind === 'ok' && filtered.length > 0 && (
        <div className="card-object-grid">
          {filtered.map(s => (
            <a
              key={s.CodeClient}
              href={href(`/societes/${encodeURIComponent(s.CodeClient)}`)}
              className={`card-object band-${classifyBand(s)}`}
            >
              <div className="card-object-header">
                <div className="card-object-avatar" aria-hidden="true">
                  <IconBuilding size={18} />
                </div>
                <div className="card-object-title-wrap">
                  <h3 className="card-object-title">{s.RaisonSociale}</h3>
                  <p className="card-object-subtitle">
                    {s.FormeJuridiqueAbregee ?? '—'}
                    {s.Ville ? ` · ${s.Ville}` : ''}
                  </p>
                </div>
                <IconChevronRight size={16} className="card-object-chevron" />
              </div>
              <div className="card-object-meta">
                <span className={`chip chip-${classifyBand(s)}`}>{bandLabel(s)}</span>
                <span className="chip-id">{s.CodeClient}</span>
                <span className="chip-siren">SIREN {fmtSiren(s.Siren)}</span>
              </div>
            </a>
          ))}
        </div>
      )}
    </>
  )
}
